/**
 * The turn and the elevator — AD-007h's produced heading rate, AD-007l's
 * re-normalisation of it, and AD-007n's vertical-plane counterpart. Split out of
 * `tunables.ts` on the established sibling-register pattern
 * (`aero-tunables.ts`, `stall-tunables.ts`, `gear-tunables.ts`), which is what
 * keeps each file one subject's worth of reading; they merge straight back into
 * `DEFAULT_TUNABLES`, so nothing downstream sees more than one table. No value
 * moved in the split.
 *
 * These belong together because they are one decision seen twice. AD-007h found
 * that the original *produces* a heading rate from bank and pull rather than
 * deriving one from lift geometry; AD-007n found the same thing in the vertical
 * plane — a full-back pull is a bounded load factor on the flight path, not a
 * rotation of the airframe — and both pay for themselves in induced drag rather
 * than getting their curvature free.
 *
 * The provenance markers are `tunables.ts`'s — VERIFIED / REPORTED,
 * CALIBRATION-PENDING, MEASURED-DOSBOX — and that module's header explains them.
 */

export const TURN_TUNABLES = {
  /**
   * AD-007h: the produced-turn-rate mechanism. Bank angle and elevator pull
   * command a heading rate directly (not lift geometry); see
   * `producedHeadingRateRadPerSec` in controls.ts.
   *
   * `turnRateCapGain` corrects the .DAT 116/118 fields' raw ÷30 conversion up
   * to the produced rate the original actually sustains. MEASURED-DOSBOX: the
   * Camel's sustained turn came out 32.6/26.7 °/s against 31.0/23.3 °/s raw —
   * a single gain of 1.096 lands both within ±4.3% (flight-tunables.md §8.1).
   */
  turnRateCapGain: 1.096,
  /**
   * Bank angle at which bank alone (stick-neutral) has bought all of the turn it
   * is ever going to buy — where the quarter-sine in
   * `producedHeadingRateRadPerSec` reaches `turnRateBankShare` and flattens. Not
   * the bank at which the *ceiling* is reached; nothing reaches the ceiling
   * without back pressure.
   *
   * MEASURED-DOSBOX, by a least-squares fit of the shape to **all thirteen** of
   * the settled hands-off banks in flight-tunables.md §8.5.1 plus §8.1 (RMS
   * 0.37 °/s, inside the sweep's own ±0.4 °/s scatter). The fit is 46.45° on
   * thirteen points and 46.43° on the twelve with §8.1's 7° datum held out, so
   * the constant is not being carried by the one point it also has to predict.
   *
   * **Why this is 46° when §8.5.1 reads the plateau as starting at ~41°.** A
   * quarter-sine is within 1.5 % of its own peak by 41.3° — 22.36 °/s against a
   * 22.70 plateau, well inside the ±0.4 °/s the traces resolve — so a sweep that
   * samples 41.31°, 41.75° and 43.33° and finds them flat is exactly what this
   * curve predicts. The two readings agree; 41° is where the curve becomes
   * indistinguishable from its asymptote, and 46° is where it formally arrives.
   */
  turnRateBankSaturationDeg: 46.45,
  /**
   * The fraction of the directional ceiling bank alone can produce, once past
   * `turnRateBankSaturationDeg`. MEASURED-DOSBOX: §8.5.1's plateau is 22.7 °/s where
   * §4.4's sustained turn *with full back pressure* is 32.6 °/s — so bank on
   * its own buys **0.70** of the directional ceiling and the pull is worth the
   * remaining 0.30, which is the whole of AD-007l's first correction.
   *
   * The share is the *plateau*, not the shape: how the curve gets there is the
   * quarter-sine `turnRateBankSaturationDeg` parameterises, which is what lets
   * this normalisation hold §8.5.1's plateau and §8.1's 5.3 °/s at 7° at the
   * same time. A linear ramp cannot — §8.5.1's own sub-knee readings are concave
   * (0.740 of the plateau at 25.75° and 0.929 at 35.33°, where a ramp to the
   * saturation bank gives 0.55 and 0.76) and a ramp anchored on the plateau
   * under-produces the small-bank end by 20 %.
   *
   * The saturation bank itself is bracketed rather than resolved on the high
   * side: keyboard aileron could not land between 43.33° and 48.69° (§8.5.6),
   * so the curve's last few degrees before the plateau are a fit, not a trace.
   */
  turnRateBankShare: 0.7,
  /**
   * Extra demand fraction full back-pressure contributes on top of bank alone.
   * MEASURED-DOSBOX, and derived *jointly* with `turnRateBankShare` rather than
   * after it (AD-007l's own instruction): bank past the knee supplies 0.70 of
   * the ceiling, so full back stick at 45° reaching §4.4's 32.6 °/s fixes this
   * at exactly the remaining **0.30**. AD-007h's 0.4 was the complement of the
   * refuted 45°-reaches-the-ceiling arithmetic.
   */
  turnRatePullGain: 0.3,
  /** Extra drag (as a CD-like coefficient on the produced heading rate squared) — the induced-drag energy cost AD-007h calls for a produced turn to still pay. CALIBRATION-PENDING; no session measured turn-induced speed loss directly. */
  turnInducedDragGain: 0.05,

  /**
   * AD-007n change 3: the load factor **full back stick** commands of the flight
   * path (`elevator-path.ts`). The elevator's counterpart of `turnRateCapGain` —
   * where AD-007h's turn fields cap the horizontal curvature a pull can buy,
   * this caps the vertical.
   *
   * MEASURED-DOSBOX, as a shape rather than as a constant. The flight-feel
   * session flew full back stick at three banks and read the pitch-attitude
   * rate: **+22 °/s at 0°, +4.5 °/s at 38°, −3.7 °/s at 70°**. A body-rate
   * elevator gives `q·cos φ` and cannot produce the negative number. Solving the
   * vertical-plane balance `γ̇ = g(n·cos φ − cos γ)/V` on each trace's own run
   * means gives **n ≈ 1.8 / 1.5 / 1.7** — three manoeuvres with nothing else in
   * common agreeing on one bounded number, which is what establishes that the
   * command is a load factor and not a rotation rate
   * (`docs/engineering/flight-feel-findings.md` §4c).
   *
   * The value shipped is the joint fit across the three traces' own rails (R4,
   * R5, R6 of AD-007n), not any one of the three: the fleet has no load-factor
   * field, so this is a port-side tunable and the next calibration session's
   * partial-stick traces are what would refine it.
   */
  elevatorLoadFactorMax: 2.05,
  /**
   * What **full forward stick** commands, the same way. Unmeasured — the
   * flight-feel session flew the elevator forward only in AD-007m's precession
   * runs, which read a yaw rate rather than a path — so this is
   * CALIBRATION-PENDING and set to the symmetric-looking value a bunt implies:
   * the wing unloaded to zero g, not pushed to a negative one, which is what
   * §4.5's "the nose merely sagged" and the absence of any measured outside
   * manoeuvre in five sessions both suggest.
   */
  elevatorLoadFactorMin: 0,
  /**
   * The induced-drag price of a **produced** pull, as a `C_D`-like coefficient
   * on the difference between the commanded and the delivered lift coefficient
   * squared (`forces.ts`). The elevator's counterpart of `turnInducedDragGain`,
   * and AD-007h's own rule applied to the vertical plane: a produced quantity
   * still pays for itself.
   *
   * It is not decoration — it is what a max-rate manoeuvre *costs*, and the
   * original charges a great deal. `PITCH1` bleeds 96.9 → 56.6 mph in 2.8 s
   * while climbing to a 49.6° attitude, and `PULL1` 93.1 → 50.0 mph in 5.3 s
   * while climbing 87 ft; read as a drag coefficient against the commanded load
   * factor those come out around **0.25**, where the wing's own physical
   * `inducedDragFactor` is 0.10. The gap is the 1990 sim charging for a pull it
   * is not aerodynamically making — the same trade the produced turn makes —
   * and this is where the port charges it.
   *
   * CALIBRATION-PENDING: fitted jointly with `elevatorLoadFactorMax` against
   * R4's and R6's speed anchors, not read off a gauge. Zero with the stick
   * centred by construction.
   */
  elevatorPullDragGain: 0.16,

  /**
   * How much slower the fleet rolls **right** than left, as a ratio
   * (`aircraft-spec.ts`). The flight-feel session flew two identical aileron
   * ramps in opposite directions and found the Camel's plateau at **50–53 °/s
   * left against 39.5 °/s right** — so §4.3's long-quoted 49.4 °/s is the *left*
   * figure, and the port has been rolling symmetrically at the left rate in both
   * directions. The SPAD XIII shows the same sign at 11 %
   * (`re-sessions/calib/NOTES.md` §5).
   *
   * CALIBRATION-PENDING and shipped as a **two-sided straddle**, on the same
   * rule `turnRateCapGain` is: two airframes disagree (1.27 and 1.11) and one
   * global constant cannot fit both, so the geometric mean sits +7 % off the
   * Camel and −6 % off the SPAD rather than fitting one exactly and missing the
   * other by 14 %. A per-airframe law wants a third aircraft first — the obvious
   * candidate shape is rotary torque (the Camel is the fleet's largest at field
   * 68 = 200 and the SPAD has none), but two points cannot separate that from a
   * constant plus a rotary bonus, which is exactly the degeneracy AD-007m's
   * fleet-scaling note flagged.
   */
  rollRightPenalty: 1.187,
} satisfies Record<string, number>;
